import { Button, Checkbox, Modal } from "@devhop/ui";
import { EditIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useState } from "react";
import type { ProductDTO, ProductVariantDTO } from "../../domain/contracts";
import {
  useBulkDeleteVariants,
  useDeleteVariant,
  useVariantsQuery,
} from "../api/queries";
import { BulkEditVariantsDialog } from "./BulkEditVariantsDialog";
import { GenerateVariantsDialog } from "./GenerateVariantsDialog";
import { VariantFormDialog } from "./VariantFormDialog";

interface ProductVariantsSectionProps {
  productId: string;
  product: ProductDTO;
}

export function ProductVariantsSection({
  productId,
  product,
}: ProductVariantsSectionProps) {
  const { data: variants = [], isLoading } = useVariantsQuery(productId);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] =
    useState<ProductVariantDTO | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [variantToDelete, setVariantToDelete] =
    useState<ProductVariantDTO | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  const deleteVariant = useDeleteVariant(productId);
  const bulkDeleteVariants = useBulkDeleteVariants(productId);

  const toggleOne = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (checked) setSelectedIds(new Set(variants.map((v) => v.id)));
      else setSelectedIds(new Set());
    },
    [variants],
  );

  const allSelected =
    variants.length > 0 && selectedIds.size === variants.length;
  const someSelected = selectedIds.size > 0;
  const headerChecked = allSelected
    ? true
    : someSelected
      ? "indeterminate"
      : false;

  const handleEdit = (variant: ProductVariantDTO) => {
    setSelectedVariant(variant);
    setEditDialogOpen(true);
  };

  const handleDelete = (variant: ProductVariantDTO) => {
    setVariantToDelete(variant);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!variantToDelete) return;
    const id = variantToDelete.id;
    deleteVariant.mutate(id, {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        setVariantToDelete(null);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      },
    });
  };

  const confirmBulkDelete = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkDeleteVariants.mutate(
      { variantIds: ids },
      {
        onSuccess: () => {
          setBulkDeleteDialogOpen(false);
          setSelectedIds(new Set());
        },
      },
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("lo-LA", {
      style: "currency",
      currency: "LAK",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const discountPercent = (price: number, compareAtPrice?: number | null) => {
    if (
      compareAtPrice == null ||
      compareAtPrice <= 0 ||
      compareAtPrice <= price
    )
      return null;

    return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card p-6">
        <h3 className="mb-4 font-semibold text-lg">Variant</h3>
        <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Variant</h3>
            <p className="text-muted-foreground text-sm">
              ຈັດການ SKU, ລາຄາ ແລະ ສິນຄ້າຄົງຄົງຕາມຕົວເລືອກ
            </p>
          </div>
          <Button onClick={() => setGenerateDialogOpen(true)}>
            <PlusIcon className="size-4" />
            ສ້າງ Variants
          </Button>
        </div>

        {variants.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-muted-foreground text-sm">
              ຍັງບໍ່ມີ Variants, ກົດປຸ່ມ Generate ເພື່ອສ້າງຈາກ Options
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {someSelected && (
              <div className="mb-3 flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setBulkEditOpen(true)}
                >
                  <PencilIcon className="size-4" />
                  ແກ້ໄຂທີ່ເລືອກ ({selectedIds.size})
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                >
                  <Trash2Icon className="size-4" />
                  ລຶບທີ່ເລືອກ ({selectedIds.size})
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIds(new Set())}
                >
                  ຍົກເລີກການເລືອກ
                </Button>
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase">
                    <Checkbox
                      checked={headerChecked}
                      onCheckedChange={(val) =>
                        toggleAll(val === true || val === "indeterminate")
                      }
                      aria-label="ເລືອກທັງໝົດ"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase">
                    SKU
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase">
                    ຕົວເລືອກ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase">
                    ລາຄາ / ລາຄາເກົ່າ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase">
                    ຕົ້ນທຶນ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase">
                    ນ້ຳໜັກ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase">
                    ສິນຄ້າຄົງຄົງ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase">
                    ການດຳເນີນການ
                  </th>
                </tr>
              </thead>
              <tbody>
                {variants.map((variant) => (
                  <tr
                    key={variant.id}
                    className="border-b last:border-0 hover:bg-muted/30"
                  >
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selectedIds.has(variant.id)}
                        onCheckedChange={(val) =>
                          toggleOne(variant.id, val === true)
                        }
                        aria-label={`ເລືອກ ${variant.sku}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">
                      {variant.sku}
                    </td>
                    <td className="px-4 py-3">
                      {variant.optionValuesDetails &&
                      variant.optionValuesDetails.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {variant.optionValuesDetails.map((opt) => (
                            <span
                              key={`${opt.optionName?.en ?? opt.optionName?.lo ?? ""}-${opt.valueName?.en ?? opt.valueName?.lo ?? ""}`}
                              className="rounded-md bg-muted px-2 py-1 text-xs"
                            >
                              {opt.valueName?.en || opt.valueName?.lo || ""}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="flex flex-col items-end gap-0.5">
                        <span>{formatPrice(variant.price)}</span>
                        {variant.compareAtPrice != null &&
                        variant.compareAtPrice > 0 ? (
                          <>
                            <span className="text-muted-foreground text-xs line-through">
                              {formatPrice(variant.compareAtPrice)}
                            </span>
                            {(() => {
                              const pct = discountPercent(
                                variant.price,
                                variant.compareAtPrice,
                              );
                              return pct != null ? (
                                <span className="font-medium text-green-600 text-xs">
                                  -{pct}%
                                </span>
                              ) : null;
                            })()}
                          </>
                        ) : null}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">
                      {formatPrice(variant.cost ?? 0)}
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">
                      {(variant.weightGrams ?? 250) / 1000} kg
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1">
                        <span
                          className={`size-2 rounded-full ${
                            (variant.quantity ?? 0) <= 0
                              ? "bg-muted-foreground/50"
                              : "bg-green-500"
                          }`}
                        />
                        <span>{variant.quantity ?? 0} ຊິ້ນ</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(variant)}
                          aria-label={`ແກ້ໄຂ ${variant.sku}`}
                        >
                          <EditIcon className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(variant)}
                          aria-label={`ລຶບ ${variant.sku}`}
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2Icon className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <GenerateVariantsDialog
        open={generateDialogOpen}
        onOpenChange={setGenerateDialogOpen}
        productId={productId}
        product={product}
        variants={variants}
      />

      <BulkEditVariantsDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        productId={productId}
        variantIds={Array.from(selectedIds)}
        onSuccess={() => setSelectedIds(new Set())}
      />

      {selectedVariant && (
        <VariantFormDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          productId={productId}
          variant={selectedVariant}
        />
      )}

      <Modal open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">ລຶບ Variant</h3>
          <p className="text-muted-foreground text-sm">
            ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບ variant <strong>{variantToDelete?.sku}</strong>?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteVariant.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteVariant.isPending}
            >
              {deleteVariant.isPending ? "ກຳລັງລຶບ..." : "ລຶບ"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
      >
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">ລຶບ Variants ຫຼາຍລາຍການ</h3>
          <p className="text-muted-foreground text-sm">
            ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບ{" "}
            <strong>{selectedIds.size}</strong> variants ທີ່ເລືອກໄວ້? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setBulkDeleteDialogOpen(false)}
              disabled={bulkDeleteVariants.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button
              variant="destructive"
              onClick={confirmBulkDelete}
              disabled={bulkDeleteVariants.isPending}
            >
              {bulkDeleteVariants.isPending ? "ກຳລັງລຶບ..." : "ລຶບທັງໝົດ"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
